import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';

import { SmartCompareProfile } from '@core/models/models';

export const LOCAL_PROFILE_STORAGE_KEY = 'commpare-local-profile-v1';
export const SHOPPING_BASKET_STORAGE_KEY = 'commpare-shopping-basket-id-v2';
export const STORE_LIST_BASKET_STORAGE_KEY = 'commpare-store-list-basket-id';
export const STORE_LIST_BASKET_CLAIM_TOKEN_STORAGE_KEY = 'commpare-store-list-basket-claim-token';
export const SHOPPING_BASKET_CLAIM_TOKEN_STORAGE_KEY = 'commpare-shopping-basket-claim-token-v2';
export const SHARED_CITY_STORAGE_KEY = 'commpare-selected-city';
export const SHOPPING_BRANCH_STORAGE_KEY = 'commpare-shopping-branch-id';

export interface LocalShoppingProfile {
  schemaVersion: 1;
  dietaryExclusions: string[];
  approvedAlternatives: Record<string, number[]>;
}

const EMPTY_PROFILE: LocalShoppingProfile = {
  schemaVersion: 1,
  dietaryExclusions: [],
  approvedAlternatives: {}
};

@Injectable({ providedIn: 'root' })
export class LocalProfileService {
  private readonly openLocationSettingsSubject = new Subject<void>();
  readonly openLocationSettings$ = this.openLocationSettingsSubject.asObservable();
  private openSettingsPending = false;

  requestOpenLocationSettings(): void {
    this.openSettingsPending = true;
    this.openLocationSettingsSubject.next();
  }

  consumeOpenLocationSettingsRequest(): boolean {
    const pending = this.openSettingsPending;
    this.openSettingsPending = false;
    return pending;
  }

  getProfile(): LocalShoppingProfile {
    const raw = this.read(LOCAL_PROFILE_STORAGE_KEY);
    if (raw == null) return this.cloneProfile(EMPTY_PROFILE);

    try {
      const parsed: unknown = JSON.parse(raw);
      if (this.isProfile(parsed)) return this.cloneProfile(parsed);
    } catch {
      // Invalid local data is ignored and replaced only after an explicit edit.
    }

    return this.cloneProfile(EMPTY_PROFILE);
  }

  getApprovedProductIds(needId: number): number[] {
    return [...(this.getProfile().approvedAlternatives[String(needId)] ?? [])];
  }

  approveAlternative(needId: number, productId: number): LocalShoppingProfile {
    if (!this.isPositiveInteger(needId) || !this.isPositiveInteger(productId)) {
      return this.getProfile();
    }

    const profile = this.getProfile();
    const key = String(needId);
    profile.approvedAlternatives[key] = [
      ...new Set([...(profile.approvedAlternatives[key] ?? []), productId])
    ];
    this.saveProfile(profile);
    return this.cloneProfile(profile);
  }

  forgetNeed(needId: number): LocalShoppingProfile {
    const profile = this.getProfile();
    delete profile.approvedAlternatives[String(needId)];
    this.saveProfile(profile);
    return this.cloneProfile(profile);
  }

  toApiProfile(
    profile = this.getProfile(),
    allowedNeedIds?: readonly number[]
  ): SmartCompareProfile {
    const allowed = allowedNeedIds ? new Set(allowedNeedIds) : null;
    const approvedAlternatives = Object.entries(profile.approvedAlternatives)
      .map(([needId, productIds]) => ({ needId: Number(needId), productIds: [...productIds] }))
      .filter(
        (item) =>
          this.isPositiveInteger(item.needId) &&
          item.productIds.length > 0 &&
          (allowed === null || allowed.has(item.needId))
      );

    return {
      approvedAlternatives,
      hardDietaryRequirements: []
    };
  }

  getShoppingBasketId(): number | null {
    const raw = this.read(SHOPPING_BASKET_STORAGE_KEY);
    if (raw == null) return null;
    const value = Number(raw);
    return this.isPositiveInteger(value) ? value : null;
  }

  setShoppingBasketId(id: number): void {
    if (this.isPositiveInteger(id)) {
      this.write(SHOPPING_BASKET_STORAGE_KEY, String(id));
    }
  }

  clearShoppingBasketId(): void {
    this.remove(SHOPPING_BASKET_STORAGE_KEY);
    this.remove(SHOPPING_BASKET_CLAIM_TOKEN_STORAGE_KEY);
  }

  getStoreListBasketId(): number | null {
    const raw = this.read(STORE_LIST_BASKET_STORAGE_KEY);
    if (raw == null) return null;
    const value = Number(raw);
    return this.isPositiveInteger(value) ? value : null;
  }

  setStoreListBasketId(id: number): void {
    if (this.isPositiveInteger(id)) {
      this.write(STORE_LIST_BASKET_STORAGE_KEY, String(id));
    }
  }

  clearStoreListBasketId(): void {
    this.remove(STORE_LIST_BASKET_STORAGE_KEY);
    this.remove(STORE_LIST_BASKET_CLAIM_TOKEN_STORAGE_KEY);
  }

  getStoreListBasketClaimToken(): string | null {
    return this.read(STORE_LIST_BASKET_CLAIM_TOKEN_STORAGE_KEY)?.trim() || null;
  }

  setStoreListBasketClaimToken(token: string | null): void {
    const normalized = token?.trim();
    if (normalized) {
      this.write(STORE_LIST_BASKET_CLAIM_TOKEN_STORAGE_KEY, normalized);
    } else {
      this.remove(STORE_LIST_BASKET_CLAIM_TOKEN_STORAGE_KEY);
    }
  }

  getShoppingBasketClaimToken(): string | null {
    return this.read(SHOPPING_BASKET_CLAIM_TOKEN_STORAGE_KEY)?.trim() || null;
  }

  setShoppingBasketClaimToken(token: string | null): void {
    const normalized = token?.trim();
    if (normalized) {
      this.write(SHOPPING_BASKET_CLAIM_TOKEN_STORAGE_KEY, normalized);
    } else {
      this.remove(SHOPPING_BASKET_CLAIM_TOKEN_STORAGE_KEY);
    }
  }

  getSelectedCity(): string {
    return this.read(SHARED_CITY_STORAGE_KEY)?.trim() ?? '';
  }

  setSelectedCity(city: string): void {
    const normalized = city.trim();
    if (normalized) {
      this.write(SHARED_CITY_STORAGE_KEY, normalized);
    } else {
      this.remove(SHARED_CITY_STORAGE_KEY);
    }
  }

  getSelectedBranchId(): number | null {
    const raw = this.read(SHOPPING_BRANCH_STORAGE_KEY);
    if (raw == null) return null;
    const value = Number(raw);
    return this.isPositiveInteger(value) ? value : null;
  }

  setSelectedBranchId(branchId: number | null): void {
    if (branchId != null && this.isPositiveInteger(branchId)) {
      this.write(SHOPPING_BRANCH_STORAGE_KEY, String(branchId));
    } else {
      this.remove(SHOPPING_BRANCH_STORAGE_KEY);
    }
  }

  private saveProfile(profile: LocalShoppingProfile): void {
    this.write(LOCAL_PROFILE_STORAGE_KEY, JSON.stringify(profile));
  }

  private isProfile(value: unknown): value is LocalShoppingProfile {
    if (!this.isRecord(value) || value['schemaVersion'] !== 1) return false;

    const exclusions = value['dietaryExclusions'];
    const alternatives = value['approvedAlternatives'];
    if (
      !Array.isArray(exclusions) ||
      !exclusions.every((item) => typeof item === 'string' && item.trim().length > 0) ||
      !this.isRecord(alternatives)
    ) {
      return false;
    }

    return Object.entries(alternatives).every(
      ([needId, productIds]) =>
        this.isPositiveInteger(Number(needId)) &&
        Array.isArray(productIds) &&
        productIds.every((id) => this.isPositiveInteger(id))
    );
  }

  private isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
  }

  private isPositiveInteger(value: unknown): value is number {
    return typeof value === 'number' && Number.isSafeInteger(value) && value > 0;
  }

  private cloneProfile(profile: LocalShoppingProfile): LocalShoppingProfile {
    return {
      schemaVersion: 1,
      dietaryExclusions: [...profile.dietaryExclusions],
      approvedAlternatives: Object.fromEntries(
        Object.entries(profile.approvedAlternatives).map(([needId, ids]) => [
          needId,
          [...ids]
        ])
      )
    };
  }

  private read(key: string): string | null {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  }

  private write(key: string, value: string): void {
    try {
      localStorage.setItem(key, value);
    } catch {
      // Storage can be unavailable in privacy modes; the current session still works.
    }
  }

  private remove(key: string): void {
    try {
      localStorage.removeItem(key);
    } catch {
      // See write().
    }
  }
}
